import { useCallback, useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from '@/i18n'
import type { TranslationResources } from '@/i18n'
import { apiService } from '@/service/api'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import Paginator from '@/components/Paginator'

/**
 * `/settings/knowledge-graph` Registry tab — Polish Stack Gap #18b.
 *
 * Surfaces the per-tenant Entity Registry contents that `kg-regen`
 * emits (the `_resolver-data.json` sidecar) plus the latest R6
 * proposal queue. Read-only today; Apply / Reject buttons render
 * disabled behind a PostHog flag (`kg_admin_apply_proposal_enabled`)
 * until the destructive backend endpoints ship in a follow-up PR
 * composed with Gap #16's auto-apply path.
 *
 * Composes with Gap #18a (backend endpoints under `/v1/kg-admin/*`).
 * If 18a hasn't shipped to dev yet every fetch 404s; the tab degrades
 * to an "endpoint unavailable" notice rather than crashing.
 */

type RegistrySection = 'aliases' | 'byCanonical' | 'prefixRules' | 'historianDevices'

interface AliasRow {
  alias: string
  canonical: string
}
interface ByCanonicalRow {
  canonical: string
  kind: string
  equipmentType?: string
  sheets?: readonly string[]
  description?: string
  aliases?: readonly string[]
}
interface PrefixRuleRow {
  shorthand: string
  canonical: string
}
interface HistorianDeviceRow {
  deviceId: string
  tagIds: string[]
  functionCode?: string
  loopNumber?: string
  canonicalMetric?: string
}

interface RegistryResponse {
  section: RegistrySection
  totalCount: number
  page: number
  limit: number
  rows: ReadonlyArray<unknown>
  sidecarMtimeMs: number
  version: number
}

interface MatcherProposal {
  canonical: string
  kind: string
  similarity: number
  reason: string
  query: string
  equipmentType?: string
  sheets?: readonly string[]
}

interface ProposalsResponse {
  proposals: ReadonlyArray<MatcherProposal>
  generatedAt: string | null
  sidecarMtimeMs: number | null
  version: number | null
  sidecarAbsent: boolean
}

const SECTION_LABELS: Record<RegistrySection, string> = {
  aliases: 'Aliases',
  byCanonical: 'Canonical records',
  prefixRules: 'Prefix rules',
  historianDevices: 'Historian devices',
}

const DEFAULT_PAGE_SIZE = 50

// Mirrors the shape returned by `useTranslation().t` (default namespaces are
// the full `(keyof TranslationResources)[]` per the i18next augmentation in
// `src/i18n/types.ts`), so the real `t` is directly assignable to these props.
type TranslateFn = TFunction<(keyof TranslationResources)[]>

export default function RegistryTab() {
  const { t } = useTranslation()
  const { enabled: applyEnabled } = useApiFeatureFlag(
    'kg_admin_apply_proposal_enabled',
    false,
  )

  const [openSections, setOpenSections] = useState<Record<RegistrySection, boolean>>({
    aliases: true,
    byCanonical: false,
    prefixRules: false,
    historianDevices: false,
  })

  const toggleSection = useCallback((section: RegistrySection) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }, [])

  return (
    <div className="space-y-6 p-4">
      <header>
        <h2 className="text-lg font-semibold">
          {t('common:knowledge_graph.registry.title', 'Entity Registry')}
        </h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-muted">
          {t(
            'common:knowledge_graph.registry.subtitle',
            'Per-tenant `_resolver-data.json` payload surfaced by the kg-admin endpoints (Gap #18a). Read-only; destructive actions land in a follow-up.',
          )}
        </p>
      </header>

      {(Object.keys(SECTION_LABELS) as RegistrySection[]).map((section) => (
        <RegistrySectionPanel
          key={section}
          section={section}
          label={SECTION_LABELS[section]}
          open={openSections[section]}
          onToggle={() => toggleSection(section)}
          t={t}
        />
      ))}

      <ProposalsPanel applyEnabled={applyEnabled} t={t} />
    </div>
  )
}

interface RegistrySectionPanelProps {
  section: RegistrySection
  label: string
  open: boolean
  onToggle: () => void
  t: TranslateFn
}

function RegistrySectionPanel({
  section,
  label,
  open,
  onToggle,
  t,
}: RegistrySectionPanelProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [data, setData] = useState<RegistryResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const url = apiService.getUrl(
        `v1/kg-admin/registry?section=${encodeURIComponent(section)}&page=${page}&limit=${pageSize}`,
      )
      const resp = await apiService.fetchOrError(url)
      const json = (await resp.json()) as RegistryResponse
      setData(json)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [section, page, pageSize])

  // Lazy fetch on first open + on page change while open.
  useEffect(() => {
    if (open) void refresh()
  }, [open, refresh])

  // Reset pagination state when the section is collapsed so a re-open
  // lands the operator on page 1, not the last page they were on
  // (greptile #12065 — close→reopen would otherwise refetch at page=N
  // with no scroll indicator that they're mid-list).
  useEffect(() => {
    if (!open) {
      setPage(1)
      setData(null)
    }
  }, [open])

  // Total pages for the Paginator. `Math.ceil` over a 0 totalCount
  // yields 0 — Paginator hides itself in that case via the
  // `totalCount > pageSize` guard at the call site.
  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / pageSize)) : 1

  return (
    <section className="rounded border border-gray-200 dark:border-dark-border">
      {/*
        Header row uses a `<div>` with two independent interactive
        children — the toggle button + the refresh button — instead
        of a nested `<span role="button">` inside the toggle button.
        The HTML spec prohibits interactive content inside `<button>`;
        the previous shape worked in Chrome but assistive tech
        reported only the outer button (greptile #12065).
      */}
      <div className="flex w-full items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-hover">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center gap-2 px-4 py-3 text-left"
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden />
          )}
          <span className="font-medium">{label}</span>
          {data ? (
            <span className="text-xs text-gray-500">
              {t('common:knowledge_graph.registry.count', '{{count}} entries').replace(
                '{{count}}',
                String(data.totalCount),
              )}
            </span>
          ) : null}
        </button>
        {open ? (
          <button
            type="button"
            onClick={() => void refresh()}
            className="mr-3 rounded px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-dark-hover"
            aria-label={t('common:knowledge_graph.registry.refresh', 'Refresh')}
          >
            <RefreshCw
              className={cn('h-3 w-3', loading && 'animate-spin')}
              aria-hidden
            />
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="border-t border-gray-200 px-4 py-3 dark:border-dark-border">
          {error ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t(
                'common:knowledge_graph.registry.fetch_error',
                'Could not load section: {{message}}',
              ).replace('{{message}}', error)}
            </p>
          ) : !data ? (
            <p className="text-sm text-gray-500">
              {t('common:knowledge_graph.registry.loading', 'Loading…')}
            </p>
          ) : (
            <SectionRows section={section} rows={data.rows} t={t} />
          )}

          {data && data.totalCount > pageSize ? (
            <div className="mt-3">
              {/*
                Paginator's actual API (frontend/web/src/components/
                Paginator.tsx:22-29): `currentPage`, `totalPages`,
                `totalItems`, `pageSize`, `setPage`, `setPageSize`.
                Previous version passed `page` / `total` /
                `onPageChange` which were silently undefined at
                runtime (greptile #12065).
              */}
              <Paginator
                currentPage={page}
                totalPages={totalPages}
                totalItems={data.totalCount}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

interface SectionRowsProps {
  section: RegistrySection
  rows: ReadonlyArray<unknown>
  t: TranslateFn
}

function SectionRows({ section, rows, t }: SectionRowsProps) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        {t('common:knowledge_graph.registry.empty', 'No entries.')}
      </p>
    )
  }
  switch (section) {
    case 'aliases':
      return (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="py-1 pr-4">alias</th>
              <th className="py-1">canonical</th>
            </tr>
          </thead>
          <tbody>
            {(rows as AliasRow[]).map((r) => (
              <tr key={r.alias} className="border-t border-gray-100 dark:border-dark-border-subtle">
                <td className="py-1 pr-4 font-mono">{r.alias}</td>
                <td className="py-1 font-mono">{r.canonical}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    case 'byCanonical':
      return (
        <ul className="space-y-2 text-sm">
          {(rows as ByCanonicalRow[]).map((r) => (
            <li
              key={r.canonical}
              className="rounded border border-gray-100 p-2 dark:border-dark-border-subtle"
            >
              <div className="font-mono font-medium">{r.canonical}</div>
              <div className="text-xs text-gray-500">
                kind: {r.kind}
                {r.equipmentType ? ` · equipmentType: ${r.equipmentType}` : ''}
                {r.sheets && r.sheets.length > 0
                  ? ` · sheets: ${r.sheets.join(', ')}`
                  : ''}
              </div>
              {r.description ? (
                <div className="text-xs">description: {r.description}</div>
              ) : null}
              {r.aliases && r.aliases.length > 0 ? (
                <div className="text-xs">
                  aliases: {r.aliases.map((a) => `"${a}"`).join(', ')}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )
    case 'prefixRules':
      return (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="py-1 pr-4">shorthand</th>
              <th className="py-1">canonical prefix</th>
            </tr>
          </thead>
          <tbody>
            {(rows as PrefixRuleRow[]).map((r) => (
              <tr
                key={`${r.shorthand}->${r.canonical}`}
                className="border-t border-gray-100 dark:border-dark-border-subtle"
              >
                <td className="py-1 pr-4 font-mono">{r.shorthand}</td>
                <td className="py-1 font-mono">{r.canonical}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    case 'historianDevices':
      return (
        <ul className="space-y-2 text-sm">
          {(rows as HistorianDeviceRow[]).map((r) => (
            <li
              key={r.deviceId}
              className="rounded border border-gray-100 p-2 dark:border-dark-border-subtle"
            >
              <div className="font-mono font-medium">{r.deviceId}</div>
              <div className="text-xs text-gray-500">
                {/*
                  tagIds is typed string[] but the runtime payload is
                  cast — schema drift on the backend (null / missing
                  field) would throw on `.join(...)` and crash the
                  whole section panel. Null-coalesce to [] before join
                  (greptile #12065).
                */}
                tagIds: {(r.tagIds ?? []).join(', ')}
              </div>
              {r.functionCode || r.loopNumber || r.canonicalMetric ? (
                <div className="text-xs text-gray-500">
                  {r.functionCode ? `fn=${r.functionCode} ` : ''}
                  {r.loopNumber ? `loop=${r.loopNumber} ` : ''}
                  {r.canonicalMetric ? `metric=${r.canonicalMetric}` : ''}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )
  }
}

interface ProposalsPanelProps {
  applyEnabled: boolean
  t: TranslateFn
}

function ProposalsPanel({ applyEnabled, t }: ProposalsPanelProps) {
  const [open, setOpen] = useState(true)
  const [data, setData] = useState<ProposalsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const url = apiService.getUrl('v1/kg-admin/r6-proposals')
      const resp = await apiService.fetchOrError(url)
      const json = (await resp.json()) as ProposalsResponse
      setData(json)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) void refresh()
  }, [open, refresh])

  // Backend always returns `proposals: Array` (even on sidecar-absent
  // → empty array), so the field is non-nullable once `data` resolves.
  // Drop the redundant optional-chain on `proposals` (CTO #12065 nit).
  // Keep the `data?` outer chain because `data` IS nullable until the
  // first fetch settles.
  const proposalCount = data ? data.proposals.length : 0

  // Apply / Reject buttons stay DISABLED in this PR — backend
  // endpoints (POST /v1/kg-admin/r6-proposals/:id/apply / /reject)
  // ship in a follow-up that composes with Gap #16's auto-apply
  // path. Wiring onClick now would surface no-op clicks even when
  // the feature flag flips ON. The flag is intentionally ignored
  // here per greptile #12065 — re-introduce its effect when the
  // backend lands, alongside an onClick handler.
  const applyTooltip = t(
    'common:knowledge_graph.registry.apply_disabled_hint',
    'Apply / Reject endpoints ship in a follow-up that composes with the R6 auto-apply path (Polish Gap #16). Tracked under kg_admin_apply_proposal_enabled in PostHog; flag has no effect on the UI until the backend lands.',
  )
  // Acknowledge the unused-for-now flag without forcing the
  // caller to drop it — flipping ON pre-backend should not blow
  // up; we just keep buttons disabled.
  void applyEnabled

  return (
    <section className="rounded border border-gray-200 dark:border-dark-border">
      {/* Two independent interactive children — toggle + refresh —
          sibling buttons, not nested. Same accessibility fix as
          RegistrySectionPanel above (greptile #12065). */}
      <div className="flex w-full items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-hover">
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className="flex flex-1 items-center gap-2 px-4 py-3 text-left"
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden />
          )}
          <span className="font-medium">
            {t('common:knowledge_graph.registry.proposals_title', 'R6 proposals')}
          </span>
          {data ? (
            <span className="text-xs text-gray-500">
              {t('common:knowledge_graph.registry.count', '{{count}} entries').replace(
                '{{count}}',
                String(proposalCount),
              )}
            </span>
          ) : null}
        </button>
        {open ? (
          <button
            type="button"
            onClick={() => void refresh()}
            className="mr-3 rounded px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-dark-hover"
            aria-label={t('common:knowledge_graph.registry.refresh', 'Refresh')}
          >
            <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} aria-hidden />
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="border-t border-gray-200 px-4 py-3 dark:border-dark-border">
          {error ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t(
                'common:knowledge_graph.registry.fetch_error',
                'Could not load proposals: {{message}}',
              ).replace('{{message}}', error)}
            </p>
          ) : !data ? (
            <p className="text-sm text-gray-500">
              {t('common:knowledge_graph.registry.loading', 'Loading…')}
            </p>
          ) : data.sidecarAbsent ? (
            <p className="text-sm text-gray-500">
              {t(
                'common:knowledge_graph.registry.proposals_empty',
                'No R6 proposals yet for this tenant (kg-regen has not produced one).',
              )}
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.proposals.map((p) => (
                <li
                  key={`${p.query}|${p.canonical}`}
                  className="flex items-start justify-between gap-3 rounded border border-gray-100 p-2 dark:border-dark-border-subtle"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-mono">
                      <span className="text-gray-500">{p.query}</span>{' '}
                      <span aria-hidden>→</span>{' '}
                      <span className="font-medium">{p.canonical}</span>{' '}
                      <span className="text-xs text-gray-500">
                        ({p.similarity.toFixed(3)})
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{p.reason}</div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {/*
                      Buttons stay disabled regardless of feature flag
                      until the apply/reject backend lands. See
                      `applyTooltip` doc above.
                    */}
                    <button
                      type="button"
                      disabled
                      title={applyTooltip}
                      className="cursor-not-allowed rounded border border-gray-200 px-2 py-1 text-xs text-gray-400 dark:border-dark-border-subtle"
                    >
                      {t('common:knowledge_graph.registry.apply', 'Apply')}
                    </button>
                    <button
                      type="button"
                      disabled
                      title={applyTooltip}
                      className="cursor-not-allowed rounded border border-gray-200 px-2 py-1 text-xs text-gray-400 dark:border-dark-border-subtle"
                    >
                      {t('common:knowledge_graph.registry.reject', 'Reject')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  )
}
